import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCheck, FiX, FiCheckCircle } from 'react-icons/fi';
import { getTrainingLesson, submitPracticeQuiz, markLessonComplete } from '../services/api';
import LessonMarkdown from '../components/LessonMarkdown';

export default function TrainingLesson() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({}); // {qid: 'A'|'B'|'C'|'D'}
  const [results, setResults] = useState(null); // {score, correct_count, total, results: [...]}
  const [submitting, setSubmitting] = useState(false);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    setLoading(true);
    setResults(null);
    setAnswers({});
    getTrainingLesson(id)
      .then(res => setLesson(res.data))
      .catch(err => setError(err.response?.data?.error || err.message || 'Failed to load lesson.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ padding: 28 }}>Loading…</div>;
  if (error) return (
    <div style={{ padding: 28 }}>
      <Link to="/training" style={{ color: '#1e40af' }}>← Training</Link>
      <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8, marginTop: 12 }}>{error}</div>
    </div>
  );
  if (!lesson) return null;

  const questions = lesson.practice_questions || [];
  const allAnswered = questions.length > 0 && questions.every(q => answers[q.id]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await submitPracticeQuiz(lesson.id, answers);
      setResults(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit quiz.');
    } finally {
      setSubmitting(false);
    }
  };

  const retake = () => {
    setResults(null);
    setAnswers({});
  };

  const markComplete = async () => {
    setMarking(true);
    try {
      await markLessonComplete(lesson.id);
      navigate(`/training/modules/${lesson.module_id}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark complete.');
    } finally {
      setMarking(false);
    }
  };

  return (
    <div style={{ padding: '24px 28px', maxWidth: 860, margin: '0 auto' }}>
      <Link
        to={`/training/modules/${lesson.module_id}`}
        style={{ color: '#1e40af', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, textDecoration: 'none' }}
      >
        <FiArrowLeft size={14} /> Back to module
      </Link>

      <h1 style={{ margin: '12px 0 6px 0', color: '#0f172a' }}>{lesson.title}</h1>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
        Estimated reading time: {lesson.estimated_minutes} min
        {lesson.completed && (
          <span style={{ color: '#16a34a', marginLeft: 12, fontWeight: 600 }}>
            <FiCheckCircle style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Completed
          </span>
        )}
      </div>

      <article style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 28px', marginBottom: 24 }}>
        <LessonMarkdown source={lesson.body} />
      </article>

      {questions.length > 0 ? (
        <section style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
          <h2 style={{ margin: '0 0 6px 0', color: '#0f172a', fontSize: 18 }}>Practice quiz</h2>
          <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#64748b' }}>
            Open-book. Retake as many times as you like — your best score is recorded.
            {lesson.best_practice_score !== null && lesson.best_practice_score !== undefined && (
              <> Your current best: <strong>{lesson.best_practice_score}%</strong> ({lesson.practice_attempts || 0} attempt{(lesson.practice_attempts || 0) === 1 ? '' : 's'}).</>
            )}
          </p>

          {questions.map((q, qi) => {
            const result = results?.results?.find(r => r.question_id === q.id);
            return (
              <div
                key={q.id}
                style={{
                  marginBottom: 18,
                  padding: 14,
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8
                }}
              >
                <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 10 }}>
                  {qi + 1}. {q.prompt}
                </div>
                {['A', 'B', 'C', 'D'].map(letter => {
                  const text = q[`choice_${letter.toLowerCase()}`];
                  if (!text) return null;
                  const selected = answers[q.id] === letter;
                  const isCorrect = result?.correct_choice === letter;
                  const isUserChoice = result?.user_choice === letter;
                  let bg = 'white';
                  let border = '#e2e8f0';
                  let color = '#0f172a';
                  if (results) {
                    if (isCorrect) {
                      bg = '#dcfce7';
                      border = '#86efac';
                    } else if (isUserChoice && !result.is_correct) {
                      bg = '#fee2e2';
                      border = '#fca5a5';
                    }
                  } else if (selected) {
                    bg = '#dbeafe';
                    border = '#93c5fd';
                  }
                  return (
                    <label
                      key={letter}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '8px 12px',
                        marginBottom: 6,
                        border: `1px solid ${border}`,
                        background: bg,
                        borderRadius: 6,
                        cursor: results ? 'default' : 'pointer',
                        color
                      }}
                    >
                      <input
                        type="radio"
                        name={`q-${q.id}`}
                        disabled={!!results}
                        checked={selected}
                        onChange={() => setAnswers(a => ({ ...a, [q.id]: letter }))}
                        style={{ marginTop: 3 }}
                      />
                      <span style={{ flex: 1, fontSize: 14, lineHeight: 1.45 }}>
                        <strong style={{ marginRight: 6 }}>{letter}.</strong>
                        {text}
                      </span>
                      {results && isCorrect && <FiCheck color="#16a34a" />}
                      {results && isUserChoice && !result.is_correct && <FiX color="#dc2626" />}
                    </label>
                  );
                })}
                {results && result?.explanation && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: 10,
                      background: result.is_correct ? '#f0fdf4' : '#fff7ed',
                      borderLeft: `4px solid ${result.is_correct ? '#16a34a' : '#f97316'}`,
                      borderRadius: 4,
                      fontSize: 13,
                      color: '#1f2937',
                      lineHeight: 1.5
                    }}
                  >
                    <strong>{result.is_correct ? 'Correct.' : 'Not quite.'} </strong>
                    {result.explanation}
                  </div>
                )}
              </div>
            );
          })}

          {!results ? (
            <button
              disabled={!allAnswered || submitting}
              onClick={submit}
              style={{
                background: allAnswered ? '#1e40af' : '#94a3b8',
                color: 'white',
                border: 'none',
                padding: '12px 22px',
                borderRadius: 8,
                cursor: allAnswered ? 'pointer' : 'not-allowed',
                fontSize: 15,
                fontWeight: 600
              }}
            >
              {submitting ? 'Grading…' : `Submit answers (${Object.keys(answers).length}/${questions.length})`}
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div
                style={{
                  background: results.score >= 75 ? '#dcfce7' : '#fef3c7',
                  color: results.score >= 75 ? '#14532d' : '#854d0e',
                  padding: '10px 16px',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                Score: {results.score}% ({results.correct_count}/{results.total})
              </div>
              <button
                onClick={retake}
                style={{
                  background: '#1e40af',
                  color: 'white',
                  border: 'none',
                  padding: '10px 18px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                Retake quiz
              </button>
              <button
                onClick={() => navigate(`/training/modules/${lesson.module_id}`)}
                style={{
                  background: '#16a34a',
                  color: 'white',
                  border: 'none',
                  padding: '10px 18px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                Done — back to module
              </button>
            </div>
          )}
        </section>
      ) : (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 24, textAlign: 'center' }}>
          {lesson.completed ? (
            <div style={{ color: '#16a34a', fontWeight: 600 }}>
              <FiCheckCircle style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Lesson complete
            </div>
          ) : (
            <button
              disabled={marking}
              onClick={markComplete}
              style={{
                background: '#16a34a',
                color: 'white',
                border: 'none',
                padding: '10px 22px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              {marking ? 'Saving…' : 'Mark lesson complete'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
